/**
 * Capacitor Native Bridge
 * Handles platform detection and native features for the mobile app.
 * In the browser (web), all functions are no-ops or use web equivalents.
 */
import axios from 'axios';

// Detect if running inside Capacitor native app
export const isNative = () => {
  return window.Capacitor !== undefined && window.Capacitor.isNativePlatform();
};

export const getPlatform = () => {
  if (!window.Capacitor) return 'web';
  return window.Capacitor.getPlatform(); // 'ios', 'android', or 'web'
};

/**
 * Get the correct base URL for payment redirects.
 * In native app: use production URL (for Mollie to redirect to)
 * In web: use current origin
 */
export const getPaymentRedirectBase = () => {
  if (isNative()) {
    return 'https://checkcheck.be';
  }
  return window.location.origin;
};

/**
 * Open an external URL (like Mollie checkout) in the system browser.
 * In native: uses Capacitor Browser plugin
 * In web: opens in a new tab
 */
export const openExternalUrl = async (url) => {
  if (isNative()) {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url, windowName: '_system' });
    } catch {
      window.open(url, '_blank');
    }
  } else {
    // Break out of any iframe (e.g. Emergent preview) — Mollie blocks X-Frame-Options
    if (window.top !== window.self) {
      window.top.location.href = url;
    } else {
      window.location.href = url;
    }
  }
};

/**
 * Initialize native plugins on app start.
 * Sets up status bar, keyboard, and deep link handling.
 */
export const initNativePlugins = async (navigateCallback) => {
  if (!isNative()) return;

  try {
    // Status Bar: dark style for our dark theme
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#F8FAFC' });
  } catch {}

  try {
    // Handle deep links (for payment return)
    const { App: CapApp } = await import('@capacitor/app');
    CapApp.addListener('appUrlOpen', (event) => {
      // event.url = 'https://checkcheck.be/payment/return?payment_id=xxx'
      const url = new URL(event.url);
      const path = url.pathname + url.search;
      if (navigateCallback && path) {
        navigateCallback(path);
      }
    });

    // Handle back button on Android
    CapApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        CapApp.exitApp();
      }
    });
  } catch {}

  try {
    // Keyboard: scroll content when keyboard appears
    const { Keyboard } = await import('@capacitor/keyboard');
    Keyboard.addListener('keyboardWillShow', (info) => {
      document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
    });
    Keyboard.addListener('keyboardWillHide', () => {
      document.body.style.setProperty('--keyboard-height', '0px');
    });
  } catch {}
};

// Marqueur plateforme pour le CSS (styles natifs spécifiques via body.cc-native)
// + header diagnostic X-CC-Platform : segmente Android/iOS dans les logs edge/backend
try {
  if (isNative()) {
    document.body.classList.add('cc-native');
    axios.defaults.headers.common['X-CC-Platform'] = getPlatform();
  }
} catch (e) { /* noop */ }
